import React, { useState } from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton as MuiIconButton, Tooltip } from '@mui/material'
import Button, { IconButton } from '../ui/Button'
import { CodeItemGroup } from '../../types/codeIndex'
import { ActiveUsersIndicator, ActiveUser } from './ActiveUsersIndicator'
import ChangesRequestedDrawer from './ChangesRequestedDrawer'
import type { Change, ResolveAppendixLabel } from './ChangesRequestedDrawer'

type HeaderData = {
  partNumber: string
  businessUnit: string
  productLine: string
  version?: string
  lastMergedText: string
  format: string
}

type CodeIndexHeaderProps = {
  header: HeaderData
  groups: CodeItemGroup[]
  published: boolean
  projectStatus?: string | null
  projectRevision?: string | null
  saving: boolean
  hasChanges: boolean
  hasUnsavedNewRows?: boolean
  hasBlockingValidationErrors?: boolean
  blockingValidationReason?: string | null
  allowBlockingActionClicks?: boolean
  undoStackLength: number
  activeUsers?: ActiveUser[]
  isConnected?: boolean
  codeIndexId?: string
  projectId?: string
  changesRequestedRefreshKey?: number
  onRefreshChangesRequested?: () => void | Promise<void>
  canViewChangesRequested?: boolean
  changesDisabledReason?: string | null
  canReviewChangesets?: boolean
  changesReviewDisabledReason?: string | null
  saveDisabledReason?: string | null
  submitForReviewDisabledReason?: string | null
  // Values the last save found unselectable; Submit stays unavailable while any remain.
  outstandingUnselectableCount?: number
  // Disagreements between the rules and valid-combination appendices; they share the Submit gate.
  outstandingAppendixDisagreementCount?: number
  mergeToMainDisabledReason?: string | null
  showMutationActions?: boolean
  showPublishedToggle?: boolean
  saveButtonLabel?: string
  savingLabel?: string
  showSaveAction?: boolean
  submitButtonLabel?: string
  onCancelProject?: () => void
  cancelProjectDisabledReason?: string | null
  canReviseRejectedChanges?: boolean
  revisionDisabledReason?: string | null
  onReviseChange?: (change: Change) => void | Promise<void>
  onGoToChange?: (change: Change) => void
  resolveAppendixLabel?: ResolveAppendixLabel
  onOpenProjects?: () => void
  onCreateProject?: () => void
  onSaveChanges: () => void
  onSubmitForReview?: () => void
  onMergeToMain?: () => void
  onUndo: () => void
}

function getProjectStatusBadgeClassName(status: string | null) {
  if (status === 'AllApproved') {
    return {
      badge: 'bg-[#E6F4EA] text-[#1E7A3F]',
      dot: 'bg-[#1E7A3F]',
    }
  }

  if (status === 'SubmittedForReview') {
    return {
      badge: 'bg-[#FFF3D8] text-[#8A5A00]',
      dot: 'bg-[#B7791F]',
    }
  }

  return {
    badge: 'bg-[#E9F2FB] text-[#1D5176]',
    dot: 'bg-[#1D5176]',
  }
}

const HEADER_ACTION_BUTTON_STYLE = { height: 32 } as const
const HEADER_ICON_ACTION_BUTTON_STYLE = { height: 32, width: 32 } as const

export const CodeIndexHeader: React.FC<CodeIndexHeaderProps> = ({
  header,
  published,
  projectStatus = null,
  projectRevision = null,
  saving,
  hasChanges,
  hasUnsavedNewRows = false,
  hasBlockingValidationErrors = false,
  blockingValidationReason = null,
  allowBlockingActionClicks = false,
  undoStackLength,
  activeUsers = [],
  isConnected = false,
  codeIndexId,
  projectId,
  changesRequestedRefreshKey = 0,
  onRefreshChangesRequested,
  canViewChangesRequested = true,
  changesDisabledReason = null,
  canReviewChangesets = false,
  changesReviewDisabledReason = null,
  saveDisabledReason = null,
  submitForReviewDisabledReason = null,
  outstandingUnselectableCount = 0,
  outstandingAppendixDisagreementCount = 0,
  mergeToMainDisabledReason = null,
  showMutationActions = true,
  showPublishedToggle = true,
  saveButtonLabel = 'Save Changes',
  savingLabel = 'Saving...',
  showSaveAction = true,
  submitButtonLabel = 'Submit for Review',
  onCancelProject,
  cancelProjectDisabledReason = null,
  canReviseRejectedChanges = false,
  revisionDisabledReason = null,
  onReviseChange,
  onGoToChange,
  resolveAppendixLabel,
  onOpenProjects,
  onCreateProject,
  onSaveChanges,
  onSubmitForReview,
  onMergeToMain,
  onUndo
}) => {
  const [changesDrawerOpen, setChangesDrawerOpen] = useState(false)
  const [mergeToMainOpen, setMergeToMainOpen] = useState(false)
  const actionDisabled = !hasChanges || saving || hasUnsavedNewRows || (hasBlockingValidationErrors && !allowBlockingActionClicks)
  const outstandingReviewCount = outstandingUnselectableCount + outstandingAppendixDisagreementCount
  const submitForReviewDisabled = saving
    || hasUnsavedNewRows
    || outstandingReviewCount > 0
    || (hasBlockingValidationErrors && !allowBlockingActionClicks)
  const actionDisabledTitle = hasBlockingValidationErrors && !allowBlockingActionClicks
    ? blockingValidationReason || 'Resolve validation errors before continuing.'
    : undefined
  const isPreviewMode = false
  const projectStatusLabel = projectStatus
    ? projectStatus.replace(/([a-z])([A-Z])/g, '$1 $2')
    : null
  const projectStatusBadgeClassName = getProjectStatusBadgeClassName(projectStatus)
  const showLiveReadOnlyActionsOnly = !showMutationActions && !isPreviewMode
  const showMergeToMainAction = showMutationActions && Boolean(onMergeToMain)
  const showProjectTransitionAction = showMergeToMainAction

  return (
    <>
      <ChangesRequestedDrawer
        open={changesDrawerOpen}
        onClose={() => setChangesDrawerOpen(false)}
        codeIndexId={codeIndexId}
        projectId={projectId}
        refreshKey={changesRequestedRefreshKey}
        hasApprovalPermission={canReviewChangesets}
        approvalDisabledReason={changesReviewDisabledReason}
        onRefresh={onRefreshChangesRequested}
        canReviseRejectedChanges={canReviseRejectedChanges}
        revisionDisabledReason={revisionDisabledReason}
        onReviseChange={onReviseChange}
        onGoToChange={onGoToChange}
        resolveAppendixLabel={resolveAppendixLabel}
      />
      <Dialog
        open={mergeToMainOpen}
        onClose={() => setMergeToMainOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: { width: 430, maxWidth: 'calc(100vw - 32px)' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 3, pb: 1 }}>
          <span className="text-lg font-semibold text-gray-900">Merge to Main</span>
          <MuiIconButton onClick={() => setMergeToMainOpen(false)} size="small" aria-label="Close">
            <span className="text-base leading-none" aria-hidden="true">x</span>
          </MuiIconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1 }}>
          <p className="mb-4 text-sm leading-5 text-gray-500">
            Are you sure you want to merge these changes to Main? Once merged, they will be live to all users.
          </p>
          <div className="text-xs font-medium text-gray-700">Details</div>
          <div className="mt-2 rounded border border-gray-200 px-3 py-2 text-sm text-gray-700">
            Revision {projectRevision || '—'}
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="secondary" onClick={() => setMergeToMainOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={saving || !onMergeToMain}
            onClick={() => {
              setMergeToMainOpen(false)
              onMergeToMain?.()
            }}
          >
            Merge to Main
          </Button>
        </DialogActions>
      </Dialog>
      <div data-pdf-codesheet-header className="flex items-center gap-8 px-6 py-3 border-b border-[#d0d7de] bg-[#F6F8FA]">
        <div className="flex gap-8 items-center flex-wrap">
          <div>
            <span className="text-xs text-[#666]">Part Number</span>
            <div className="font-semibold text-sm">{header.partNumber || '--'}</div>
          </div>
          {projectStatusLabel && (
            <div className="flex min-w-[72px] items-center self-stretch pt-3">
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${projectStatusBadgeClassName.badge}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${projectStatusBadgeClassName.dot}`} aria-hidden="true" />
                {projectStatusLabel}
              </span>
            </div>
          )}
          <div><span className="text-xs text-[#666]">Business Unit</span><div className="font-semibold text-sm">{header.businessUnit || '--'}</div></div>
          <div><span className="text-xs text-[#666]">Format</span><div className="font-semibold text-sm">{header.format || '--'}</div></div>
          <div><span className="text-xs text-[#666]">Product Line</span><div className="font-semibold text-sm">{header.productLine || '--'}</div></div>
          <div><span className="text-xs text-[#666]">Version</span><div className="font-semibold text-sm">{header.version || '--'}</div></div>
          <div><span className="text-xs text-[#666]">Last Merged</span><div className="font-semibold text-sm">{header.lastMergedText || '--'}</div></div>
        </div>
        {/* data-pdf-omit: dropped from the PDF header clone, which keeps only the metadata. */}
        <div data-pdf-omit className="ml-auto flex items-center gap-3">
          {!showLiveReadOnlyActionsOnly && activeUsers.length > 0 && (
            <div className="flex items-center gap-2 pr-3 border-r border-[#d0d7de]">
              <ActiveUsersIndicator users={activeUsers} maxVisible={3} size={28} />
            </div>
          )}
          {showPublishedToggle && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#666]">Published</span>
              <label className="relative inline-block w-10 h-5">
                <input type="checkbox" checked={published} disabled className="opacity-0 w-0 h-0" />
                <span className={`absolute cursor-not-allowed inset-0 border border-[#CBD5E1] rounded-[20px] transition-colors duration-200 ${published ? 'bg-[#9BBCE0]' : 'bg-[#E5E7EB]'}`} />
                <span className={`absolute h-4 w-4 top-0.5 bg-[#F8FAFC] border border-[#CBD5E1] rounded-full transition-[left] duration-200 ${published ? 'left-[22px]' : 'left-1'}`} />
              </label>
            </div>
          )}
          {!isPreviewMode && showMergeToMainAction && (
            <Tooltip title={mergeToMainDisabledReason || ''}>
              <span>
                <Button size="sm" variant="primary"
                  style={HEADER_ACTION_BUTTON_STYLE}
                  disabled={saving || Boolean(mergeToMainDisabledReason)}
                  title={mergeToMainDisabledReason || undefined}
                  onClick={() => setMergeToMainOpen(true)}>
                  {saving ? 'Merging...' : 'Merge to Main'}
                </Button>
              </span>
            </Tooltip>
          )}
          {!isPreviewMode && showMutationActions && showSaveAction && !showProjectTransitionAction && (
            <Tooltip title={actionDisabledTitle || saveDisabledReason || ''}>
              <span>
                <Button size="sm" variant="secondary"
                  style={HEADER_ACTION_BUTTON_STYLE}
                  disabled={actionDisabled || Boolean(saveDisabledReason)}
                  title={actionDisabledTitle || saveDisabledReason || undefined}
                  onClick={onSaveChanges}>
                  {saving ? savingLabel : saveButtonLabel}
                </Button>
              </span>
            </Tooltip>
          )}
          {!isPreviewMode && showMutationActions && !showProjectTransitionAction && onSubmitForReview && (
            <Tooltip title={actionDisabledTitle || submitForReviewDisabledReason || ''}>
              <span>
                <Button size="sm" variant="primary"
                  style={HEADER_ACTION_BUTTON_STYLE}
                  disabled={submitForReviewDisabled || Boolean(submitForReviewDisabledReason)}
                  title={actionDisabledTitle || submitForReviewDisabledReason || undefined}
                  onClick={onSubmitForReview}>
                  {saving ? 'Submitting...' : submitButtonLabel}
                </Button>
              </span>
            </Tooltip>
          )}
          {!isPreviewMode && onCancelProject && (
            <Tooltip title={cancelProjectDisabledReason || ''}>
              <span>
                <Button
                  size="sm"
                  variant="secondary"
                  style={HEADER_ACTION_BUTTON_STYLE}
                  disabled={saving || Boolean(cancelProjectDisabledReason)}
                  onClick={onCancelProject}
                >
                  Cancel Project
                </Button>
              </span>
            </Tooltip>
          )}
          <div className="flex items-center gap-2">
            {onOpenProjects && (
              <Button
                size="sm"
                variant="secondary"
                style={HEADER_ACTION_BUTTON_STYLE}
                onClick={onOpenProjects}
                leftIcon={(
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                    <path d="M3 3v5h5" />
                    <path d="M12 7v5l4 2" />
                  </svg>
                )}
              >
                History
              </Button>
            )}
            {onCreateProject && (
              <Button
                size="sm"
                variant="primary"
                style={HEADER_ACTION_BUTTON_STYLE}
                onClick={onCreateProject}
                leftIcon={<span aria-hidden="true" className="text-base leading-none">+</span>}
              >
                New Project
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {showMutationActions && !isPreviewMode && (
              <Tooltip title={changesDisabledReason || 'View Changes Requested'}>
                <span>
                  <IconButton
                    size="sm"
                    style={HEADER_ICON_ACTION_BUTTON_STYLE}
                    icon={<img src="/book.svg" alt="Changes" className="w-5 h-5" />}
                    variant="primary"
                    onClick={() => setChangesDrawerOpen(true)}
                    disabled={!canViewChangesRequested}
                    title={changesDisabledReason || 'View Changes Requested'}
                  />
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
